import {
  HHAlert,
  HHButton,
  HHCheckbox,
  HHFormControlLabel,
  HHStack,
  HHSwitch,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useHingeSelectClient } from '../../api';
import { ContractServiceProvider } from '../../api/types';
import { SkeletonLines } from '../components/feedback/loading-skeleton';
import { IsoDateField } from '../components/inputs/iso-date-field';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import { STEP_LABELS } from '../constants';
import { ContractInput, CspRow } from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { protoDateToIso } from '../utils/iso-to-protodate';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
};

interface CspsInnerProps {
  input: ContractInput;
}

const CspsInner = ({ input }: CspsInnerProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const navigate = useNavigate();

  useStepHeader({
    description:
      'Bind the (contract, fee_schedule) pair to specific KG entities. Default: one CSP per MerchantLocation, all included, dates inherited from the contract.',
  });

  const csps = state.decisions.csps;
  // PR 2 Slice 1: contract decisions are per-TIN keyed; canonical-TIN read.
  const tin = input.locations[0]?.tax_id ?? '';
  const contract = state.decisions.contract[tin];
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');

  const reusedContractId =
    contract?.mode === 'reuse' ? contract.existingId : null;

  const fetchedFor = useRef<string | null>(null);
  const [existingCsps, setExistingCsps] = useState<ContractServiceProvider[]>(
    [],
  );
  const [cspLoad, setCspLoad] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  const [cspError, setCspError] = useState<string | null>(null);

  // Pull already-attached service providers when reusing a contract so the
  // operator sees what's already wired up before adding more rows.
  useEffect(() => {
    if (!reusedContractId) return;
    if (fetchedFor.current === reusedContractId) return;
    fetchedFor.current = reusedContractId;
    setCspLoad('loading');
    void (async (): Promise<void> => {
      try {
        const result = await client.getContractServiceProviders({
          contractId: reusedContractId,
        });
        setExistingCsps(result.contractServiceProviders ?? []);
        setCspLoad('loaded');
      } catch (e) {
        setCspError((e as Error).message);
        setCspLoad('error');
      }
    })();
  }, [client, reusedContractId]);

  // Selected-location set: checked input rows + checked existing rows. The
  // Locations step guarantees these two sets are disjoint by merchantLocationId
  // (it strips overlapping ids from `existing` at write time), so no dedup is
  // needed here. Existing-only rows carry an `existing:` sentinel prefix on
  // `id` so commit can distinguish them from input-keyed rows.
  const locationRows: CspRow[] = useMemo(() => {
    if (contract == null) return [];
    const endDate =
      contract.mode === 'create' && contract.openEnded
        ? undefined
        : (contract.effectiveEnd ?? undefined);
    const rows: CspRow[] = [];
    for (const l of input.locations) {
      const key = `${l.address.locality}|${l.address.address_line_1}`;
      if (state.decisions.locationSelection.input[key] === false) continue;
      rows.push({
        id: key,
        included: true,
        effectiveStart: contract.effectiveStart,
        effectiveEnd: endDate,
      });
    }
    for (const [mlId, checked] of Object.entries(
      state.decisions.locationSelection.existing,
    )) {
      if (!checked) continue;
      rows.push({
        id: `existing:${mlId}`,
        included: true,
        effectiveStart: contract.effectiveStart,
        effectiveEnd: endDate,
      });
    }
    return rows;
  }, [contract, input.locations, state.decisions.locationSelection]);

  // Reconcile (init or re-init) the CSP rows whenever the selected-location
  // set drifts from what's stored. Preserves user-edited dates / included on
  // rows that survive; new rows get defaults; removed rows drop out. Mode is
  // preserved across reconciles since the access-point toggle lives here.
  useEffect(() => {
    if (contract == null) return;
    if (locationRows.length === 0) return;
    const desiredIds = new Set(locationRows.map(r => r.id));
    const existingIds = new Set((csps?.rows ?? []).map(r => r.id));
    const setsMatch =
      csps != null &&
      desiredIds.size === existingIds.size &&
      [...desiredIds].every(id => existingIds.has(id));
    if (setsMatch) return;
    setState(s => {
      const priorById = new Map(
        (s.decisions.csps?.rows ?? []).map(r => [r.id, r]),
      );
      return {
        ...s,
        decisions: {
          ...s.decisions,
          csps: {
            mode: s.decisions.csps?.mode ?? 'location',
            rows: locationRows.map(lr => priorById.get(lr.id) ?? lr),
          },
        },
      };
    });
  }, [contract, csps, locationRows, setState]);

  const setMode = (mode: 'location' | 'access_point'): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        csps:
          s.decisions.csps != null
            ? { ...s.decisions.csps, mode }
            : s.decisions.csps,
      },
    }));
  };

  const updateRow = (id: string, patch: Partial<CspRow>): void => {
    setState(s => {
      if (s.decisions.csps == null) return s;
      return {
        ...s,
        decisions: {
          ...s.decisions,
          csps: {
            ...s.decisions.csps,
            rows: s.decisions.csps.rows.map(r =>
              r.id === id ? { ...r, ...patch } : r,
            ),
          },
        },
      };
    });
  };

  const applyBulkDates = (): void => {
    setState(s => {
      if (s.decisions.csps == null) return s;
      return {
        ...s,
        decisions: {
          ...s.decisions,
          csps: {
            ...s.decisions.csps,
            rows: s.decisions.csps.rows.map(r =>
              r.included
                ? {
                    ...r,
                    effectiveStart: bulkStart || r.effectiveStart,
                    effectiveEnd: bulkEnd || r.effectiveEnd,
                  }
                : r,
            ),
          },
        },
      };
    });
    setBulkOpen(false);
  };

  const includedCount = csps?.rows.filter(r => r.included).length ?? 0;
  const canContinue = includedCount > 0;
  const disabledInfo: DisabledInfo | undefined = canContinue
    ? undefined
    : {
        message: 'Include at least one CSP to continue.',
        severity: 'info',
      };

  return (
    <HHStack spacing={3}>
      {reusedContractId != null && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <HHStack spacing={1}>
            <HHTypography hhVariant="subtitle2">
              Already attached to this contract
            </HHTypography>
            {cspLoad === 'loading' && <SkeletonLines lines={2} />}
            {cspLoad === 'error' && (
              <HHAlert hhVariant="outlined" severity="warning">
                Couldn&apos;t fetch existing service providers: {cspError}.
              </HHAlert>
            )}
            {cspLoad === 'loaded' && existingCsps.length === 0 && (
              <HHTypography hhVariant="body2" sx={{ opacity: 0.6 }}>
                No service providers are attached to this contract yet.
              </HHTypography>
            )}
            {cspLoad === 'loaded' && existingCsps.length > 0 && (
              <HHStack spacing={0.5} sx={monoText}>
                {existingCsps.map(c => (
                  <HHStack
                    key={c.id ?? `${c.knowledgeEntityId}:${c.feeScheduleId}`}
                    direction="row"
                    spacing={2}
                  >
                    <span style={{ width: 160, opacity: 0.7 }}>
                      {c.knowledgeEntityType.replace(
                        'SERVICE_PROVIDER_TYPE_',
                        '',
                      )}
                    </span>
                    <span style={{ flex: 1 }}>{c.knowledgeEntityId}</span>
                    <span style={{ width: 220, opacity: 0.65 }}>
                      {protoDateToIso(c.effectiveStart)} →{' '}
                      {c.effectiveEnd
                        ? protoDateToIso(c.effectiveEnd)
                        : 'open-ended'}
                    </span>
                  </HHStack>
                ))}
              </HHStack>
            )}
            <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
              Rows below add to this set. Duplicate (entity_id, contract,
              fee_schedule) pairs dedupe at commit time.
            </HHTypography>
          </HHStack>
        </Paper>
      )}

      <HHStack direction="row" spacing={2} alignItems="center">
        <HHFormControlLabel
          label="Assign by AccessPoint (per-practitioner)"
          control={
            <HHSwitch
              hhVariant="variant-bypass"
              checked={csps?.mode === 'access_point'}
              disabled={!state.decisions.createAccessPoints}
              onChange={(_, v): void =>
                setMode(v ? 'access_point' : 'location')
              }
            />
          }
        />
        {state.decisions.createAccessPoints ? (
          <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
            AccessPoints will be created on commit. Switch on to use them here.
          </HHTypography>
        ) : (
          <HHStack direction="row" spacing={1} alignItems="center">
            <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
              AccessPoints are off.
            </HHTypography>
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              color="primary"
              size="small"
              onClick={(): void => navigate('../practitioners')}
            >
              Turn on in {STEP_LABELS.practitioners} →
            </HHButton>
          </HHStack>
        )}
        <span style={{ flex: 1 }} />
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          color="primary"
          onClick={(): void => setBulkOpen(true)}
        >
          Bulk edit dates
        </HHButton>
      </HHStack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>Include</TableCell>
              <TableCell sx={{ width: 140 }}>Type</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell sx={{ width: 140 }}>Effective start</TableCell>
              <TableCell sx={{ width: 140 }}>Effective end</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(csps?.rows ?? []).map(r => {
              // Two row-id shapes: input rows (`${locality}|${address}`) and
              // existing-table-only rows (`existing:${merchantLocationId}`).
              // For the latter we read the cached MerchantLocation by id to
              // surface a human label (displayName + npi2) instead of a UUID.
              let label: string;
              let sublabel: string;
              if (r.id.startsWith('existing:')) {
                const mlId = r.id.slice('existing:'.length);
                let ml:
                  | { displayName?: string; name?: string; npi2?: string }
                  | undefined;
                for (const list of Object.values(
                  state.resolutions.existingMerchantLocations,
                )) {
                  const hit = (list ?? []).find(x => x.id === mlId);
                  if (hit) {
                    ml = hit;
                    break;
                  }
                }
                label = ml?.displayName ?? ml?.name ?? 'Existing location';
                sublabel = `NPI-2 ${ml?.npi2 ?? '—'} · ${mlId}`;
              } else {
                const [city, address] = r.id.split('|');
                label = city;
                sublabel = address;
              }
              return (
                <TableRow key={r.id}>
                  <TableCell>
                    <HHCheckbox
                      hhVariant="variant-bypass"
                      checked={r.included}
                      onChange={(_, v): void =>
                        updateRow(r.id, { included: v })
                      }
                    />
                  </TableCell>
                  <TableCell sx={monoText}>
                    {csps?.mode === 'access_point' ? 'AccessPoint' : 'Location'}
                  </TableCell>
                  <TableCell>
                    <HHStack spacing={0}>
                      <HHTypography hhVariant="body2">{label}</HHTypography>
                      <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                        {sublabel}
                      </HHTypography>
                    </HHStack>
                  </TableCell>
                  <TableCell>
                    <IsoDateField
                      label=""
                      size="small"
                      value={r.effectiveStart}
                      onChange={(v): void =>
                        updateRow(r.id, { effectiveStart: v ?? '' })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <IsoDateField
                      label=""
                      size="small"
                      value={r.effectiveEnd}
                      onChange={(v): void =>
                        updateRow(r.id, { effectiveEnd: v ?? undefined })
                      }
                      minDate={r.effectiveStart}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
        {includedCount} of {csps?.rows.length ?? 0} included
      </HHTypography>

      <Dialog open={bulkOpen} onClose={(): void => setBulkOpen(false)}>
        <DialogTitle>Bulk edit dates</DialogTitle>
        <DialogContent>
          <HHStack spacing={2} sx={{ pt: 1, minWidth: 320 }}>
            <IsoDateField
              label="Effective start"
              fullWidth
              value={bulkStart}
              onChange={(v): void => setBulkStart(v ?? '')}
            />
            <IsoDateField
              label="Effective end (blank = open-ended)"
              fullWidth
              value={bulkEnd}
              onChange={(v): void => setBulkEnd(v ?? '')}
              minDate={bulkStart}
            />
            <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
              Applies only to included rows.
            </HHTypography>
          </HHStack>
        </DialogContent>
        <DialogActions>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            color="primary"
            onClick={(): void => setBulkOpen(false)}
          >
            Cancel
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            color="primary"
            onClick={applyBulkDates}
          >
            Apply
          </HHButton>
        </DialogActions>
      </Dialog>

      <StepNav
        id="csps"
        canContinue={canContinue}
        disabledInfo={disabledInfo}
      />
    </HHStack>
  );
};

export const CspsStep = (): JSX.Element => {
  const { state } = useWizardState();
  if (state.input == null) return <Navigate to="../intake" replace />;
  return <CspsInner input={state.input} />;
};
